import React, {
  createContext, useCallback, useEffect, useState,
} from 'react';

import AccessAlarmOutlined from '@mui/icons-material/AccessAlarmOutlined';
import EmojiFoodBeverageOutlined from '@mui/icons-material/EmojiFoodBeverageOutlined';
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import MobileOffOutlined from '@mui/icons-material/MobileOffOutlined';
import {
  CircularProgress, Snackbar,
} from '@mui/material';
import { AlertColor } from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  useLocation, useNavigate,
} from 'react-router-dom';

import {
  getIsUserAuthenticated,
} from '../../services/auth';
import { useGetCurrentUserQuery } from '../../services/users';
import useUserGroups from '../../utils/react/hooks/useUserGroups';
import Button from '../Button';
import { ColorHex } from '../ProfileIcon/types';

import AppBar from './AppBar';
import Drawer from './Drawer';
import {
  ContentContainer,
  IconBorder,
  IconContainer,
  LoadingContainer,
  Main,
  MessageContainer,
  MessageContent,
  MessageTitle,
  Nav,
  SnackbarAlert,
} from './index.styles';
import { hasOverlap } from './utils';

interface MessageProps {
  message1: string,
  message2: string,
  title: string,
  icon: React.ReactElement,
}

const Message = ({ message1, message2, title, icon } : MessageProps) => (
  <MessageContainer>
    <IconBorder>
      <IconContainer>
        {icon}
      </IconContainer>
    </IconBorder>
    <MessageTitle variant="h5">{title}</MessageTitle>
    <MessageContent variant="body2">{message1}<br />{message2}</MessageContent>
    <Button size="medium" href="/">
      Return to Dashboard
    </Button>
  </MessageContainer>
);

export const NoPermissionMessage = () => (
  <Message
    title="No Access"
    message1="You do not have access to this content."
    message2="If you believe this is a mistake, please let us know."
    icon={<LockOutlined fontSize="large" />}
  />
);

export const NotOnMobileMessage = () => (
  <Message
    title="Not Available on Mobile"
    message1="This content is not available on mobile."
    message2="Please view the content on desktop or tablet."
    icon={<MobileOffOutlined fontSize="large" />}
  />
);

interface ComingSoonMessageProps {
  dueDate?: string,
}

export const ComingSoonMessage = ({ dueDate }: ComingSoonMessageProps) => (
  <Message
    title="Coming Soon"
    message1={dueDate ?
      `This feature will be available in ${dueDate}.`
      : 'This feature is not available yet.'}
    message2="Follow @cognito.tuition for updates!"
    icon={<AccessAlarmOutlined fontSize="large" />}
  />
);

export const NoContentMessage = () => (
  <Message
    title="Nothing Here Yet"
    message1="There is no content available to you here yet."
    message2="Check back again soon!"
    icon={<HelpOutlineOutlined fontSize="large" />}
  />
);

export const AllFinishedMessage = () => (
  <Message
    title="All Finished!"
    message1={'You\'ve finished everything here!'}
    message2="Pour yourself a nice cup of tea for a job well done."
    icon={<EmojiFoodBeverageOutlined fontSize="large" />}
  />
);

export const NotFoundMessage = () => (
  <Message
    title="Nothing Here! (404)"
    message1="The page you are looking for doesn't exist or has been moved."
    message2="Go back to navigate to what you're looking for!"
    icon={<HelpOutlineOutlined fontSize="large" />}
  />
);

interface LoadingWrapperProps {
  children?: React.ReactElement | React.ReactElement[],
  decorate?: boolean,
  loading: boolean,
}

export const LoadingWrapper =
({ children, loading, decorate = false }: LoadingWrapperProps) =>
  (loading ? (
    <LoadingContainer decorate={decorate}>
      <CircularProgress />
    </LoadingContainer>
  ) : (
  // react/jsx-no-useless-fragment is disabled here as LoadingWrapper must return a single element
  // and `children` may be an array of elements
  // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {children}
    </>
  ));

type SnackbarContextValue = (message: string, type?: AlertColor) => void;
type SnackbarContextDefault = () => void;

export const SnackbarContext =
  createContext<SnackbarContextValue | SnackbarContextDefault>(() => {});

interface SnackbarProviderProps {
  children: React.ReactElement | React.ReactElement[],
}

const SnackbarProvider = ({ children } : SnackbarProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [type, setType] = useState<AlertColor>('info');

  const closeSnackbar = (event_?: any, reason?: string) => {
    if (reason === 'clickaway') return;
    setIsOpen(false);
  };

  const createSnackbar = useCallback((newMessage: string, newType?: AlertColor) => {
    setMessage(newMessage);
    setType(newType || 'info');
    setIsOpen(true);
  }, []);

  return (
    <SnackbarContext.Provider value={createSnackbar}>
      {children}
      <Snackbar
        open={isOpen}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <SnackbarAlert onClose={closeSnackbar} severity={type}>
          {message}
        </SnackbarAlert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

interface ContentProps {
  children: React.ReactElement | React.ReactElement[],
  decorate: boolean,
  loading: boolean,
  userGroups?: string[],
  userFirstName?: string,
  userLastName?: string,
  userColor?: ColorHex,
  userImage?: string,
}

const Content = ({ children,
  decorate,
  loading,
  userGroups,
  userFirstName,
  userLastName,
  userColor,
  userImage }: ContentProps) => {
  const theme = useTheme();

  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const [drawerOpen, setDrawerOpen] = useState(isMd);

  const closeDrawer = () => setDrawerOpen(false);
  const openDrawer = () => setDrawerOpen(true);
  const toggleDrawer = () => setDrawerOpen(!drawerOpen);

  useEffect(() => {
    if (!isMd) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }, [isMd]);

  return (decorate ? (
    <ContentContainer>
      <AppBar
        position="fixed"
        onDrawerButtonClick={toggleDrawer}
        drawerOpen={drawerOpen}
      />
      <Nav component="nav">
        <Drawer
          open={drawerOpen}
          onClose={closeDrawer}
          mode={isMd ? 'medium' : 'small'}
          userGroups={userGroups}
          firstName={userFirstName}
          lastName={userLastName}
          profileColor={userColor}
          profileImage={userImage}
        />
      </Nav>
      <Main
        component="main"
        open={drawerOpen}
        $isMd={isMd}
      >
        <LoadingWrapper loading={loading} decorate={decorate}>
          {children}
        </LoadingWrapper>
      </Main>
    </ContentContainer>
  ) : (
    <LoadingWrapper loading={loading} decorate={decorate}>
      {children}
    </LoadingWrapper>
  ));
};

interface PageLayoutProps {
  children: React.ReactElement | React.ReactElement[],
  /**
   * Page title
   */
  title: string,
  /**
   * Redirect paths
   */
  redirects?: {
    onAuthRedirect: string,
    onAuthlessRedirect?: undefined
    onUnauthorisedRedirect?: string,
  } | {
    onAuthRedirect?: undefined,
    onAuthlessRedirect: string
    onUnauthorisedRedirect?: string,
  },
  /**
   * The page remaing in loading mode *at least* while this prop is `true`
   */
  loading?: boolean,
  /**
   * If `true`, page decorations such as the AppBar are rendered in
   */
  decorate?: boolean,
  /**
   * If `true`, all redirects are cancelled
   */
  disableRedirect?: boolean,
  /**
   * Redirects user if user is not in one of the following groups
   */
  authorisedGroups?: string[],
  /**
   * Redirects user if user does not have all of the listed permissions
   */
  requiredPermissions?: string[],
}

export const PageLayout = ({
  children,
  redirects,
  loading: isPageLoading = false,
  decorate = false,
  disableRedirect = false,
  title,
  authorisedGroups,
  requiredPermissions,
}: PageLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const groups = useUserGroups();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isAuthorised, setIsAuthorised] = useState<boolean | null>(null);
  const [userGroups, setUserGroups] = useState<string[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  // Get user details
  const { data: user } = useGetCurrentUserQuery();

  // Check the current authentication state
  useEffect(() => {
    // Update the Browser-recognised page title
    document.title = title ? `${title} | Cognito Online` : 'Cognito Online';

    // Check if the user is authenticated to determine redirects
    const checkAuth = async () => {
      const authState = await getIsUserAuthenticated();
      setIsAuthenticated(authState);
    };
    checkAuth();
  }, [title]);

  // Stop loading and fetch user groups & permissions once the authentication state is fetched
  useEffect(() => {
    if (isAuthenticated !== null && !isPageLoading) {
      setLoading(false);
      if (isAuthenticated) {
        setUserGroups(groups);
      }
    }
  }, [isAuthenticated, groups]);

  useEffect(() => {
    // Returns true if user is in one of the groups specified
    // If authorisedGroups / authorisedPermissions is not specified,
    // then it is assumed that the page is accessible by all
    const isNoAuthRequired = authorisedGroups === undefined && requiredPermissions === undefined;
    const isInAuthGroups = authorisedGroups
      && userGroups && hasOverlap(userGroups, authorisedGroups);
    const isUserAuthorised = isNoAuthRequired || isInAuthGroups;

    // We allow isAuthorised to be null, as this allows the page to be rendered
    // while the user groups are being fetched, and the redirect is only handled
    // once the user groups are fetched
    setIsAuthorised(isUserAuthorised ?? null);
  }, [userGroups, authorisedGroups, requiredPermissions]);

  useEffect(() => {
    // AWS S3 static website hosting does not support redirects, so we need to
    // manually redirect the user if they have a hash in their URL
    const path = (/#!(\/.*)$/.exec(location.hash) || [])[1];
    if (path) navigate(path, { replace: true });
  }, [location]);

  // Perform redirects based on authentication / authorisation state
  useEffect(() => {
    const redirectOnAuth = !!redirects?.onAuthRedirect
    && isAuthenticated === true;
    const redirectOnAuthless = !!redirects?.onAuthlessRedirect
   && isAuthenticated === false;

    if (!disableRedirect) {
      if (redirectOnAuth) navigate(redirects?.onAuthRedirect, { replace: true });
      // If not authenticated, redirect to the sign in page with the current path as a
      // redirect parameter
      if (redirectOnAuthless) navigate(`${redirects?.onAuthlessRedirect}?redirect=${location.pathname}`, { replace: true });
      if (isAuthorised === false) navigate(redirects?.onUnauthorisedRedirect || '/', { replace: true });
    }
  }, [disableRedirect, isAuthorised, isAuthenticated, redirects]);

  if (isAuthenticated === null) {
    return (
      <LoadingWrapper loading />
    );
  }

  return (
    <SnackbarProvider>
      <Content
        decorate={decorate}
        loading={loading}
        userFirstName={user?.data.firstName}
        userLastName={user?.data.lastName}
        userColor={user?.data.profileIcon?.color}
        userImage={user?.data.profileIcon?.image}
        userGroups={userGroups}
      >
        {children}
      </Content>
    </SnackbarProvider>
  );
};

interface AuthPageLayoutProps {
  children: React.ReactElement | React.ReactElement[],
  title: string,
  authorisedGroups?: string[],
  requiredPermissions?: string[],
}

export const AuthPageLayout =
({ children, title, authorisedGroups, requiredPermissions }: AuthPageLayoutProps) => (
  <PageLayout
    redirects={{ onAuthRedirect: '/' }}
    title={title}
    authorisedGroups={authorisedGroups}
    requiredPermissions={requiredPermissions}
  >
    {children}
  </PageLayout>
);

interface ProtectedPageLayoutProps {
  children: React.ReactElement | React.ReactElement[],
  authorisedGroups?: string[],
  requiredPermissions?: string[],
  title: string,
}

export const ProtectedPageLayout =
  ({ children, title, authorisedGroups, requiredPermissions }: ProtectedPageLayoutProps) => (
    <PageLayout
      redirects={{ onAuthlessRedirect: '/signin' }}
      decorate
      title={title}
      authorisedGroups={authorisedGroups}
      requiredPermissions={requiredPermissions}
    >
      {children}
    </PageLayout>
  );
